import type { HelpStep } from "newui/components/ui/Help";
import { TOUR_USER_INVITED } from "./tourFixtures";

/**
 * Guided tour steps for "Manage a user's roles" (PRO-730 topic chooser).
 *
 * Flow (D10 rewrite — same five narrative beats the old per-user role drawer
 * tour used, retargeted at `ManageRolesModal`):
 *   1. Highlight the dummy row's "Manage roles" action -> open the modal for
 *      it.
 *   2. The modal as a whole.
 *   3. The Role | Access Level | Item table (current roles + bulk delete).
 *   4. The Add button — the entry point into granting a new role (the tour
 *      stops here rather than following it into `AddAccessFlow`; that flow
 *      has its own "What do I do?" trigger, see `addAccessSteps.tsx`).
 *   5. Close.
 *
 * Like `userListSteps`, this never drives a real mutation: the modal is
 * opened against the dummy `TOUR_USER_INVITED`/`TOUR_USER_ASSIGNMENT` shell
 * (see `UserListPage.openManageRolesForTour`), and `ManageRolesModal` itself
 * refuses to fire its bulk-delete mutation for a negative user id (PRO-730).
 */
export interface ManageRolesSteps {
	closeManageRoles: () => void;
	openManageRoles: () => void;
}

export const buildManageRolesSteps = ({
	openManageRoles,
	closeManageRoles,
}: ManageRolesSteps): HelpStep[] => [
	{
		target: `[data-testid="user-action-manage-roles-${TOUR_USER_INVITED.id}"]`,
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Manage a user&apos;s roles</h3>
				<p>
					Click the shield icon on any row to open that user&apos;s roles. We&apos;ll
					open it for you on the next step.
				</p>
			</div>
		),
		placement: "left",
		disableBeacon: true,
		onAfterStep: () => {
			openManageRoles();
		},
	},
	{
		target: '[data-testid="manage-roles-modal"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Manage roles</h3>
				<p>See every role this user currently holds, add a new one, or remove any of these.</p>
			</div>
		),
		placement: "left",
		disableBeacon: true,
		spotlightPadding: 12,
	},
	{
		target: '[data-testid="manage-roles-table"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Current roles</h3>
				<p>
					Every role currently assigned is listed here, with its access level and the
					item it applies to. Check any rows and click Delete to remove them.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
	{
		target: '[data-testid="manage-roles-add"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Add a role</h3>
				<p>
					Grant this user a new role — company-wide, or scoped to specific portfolios,
					projects or RFx events.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
	{
		target: '[data-testid="manage-roles-close"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Close</h3>
				<p>Every change here saves immediately — Close just dismisses this view.</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
		onAfterStep: () => {
			closeManageRoles();
		},
	},
];
